import { toUpper } from 'lodash';
import numeral from 'numeral';

import { DEFAULT_PLACEHOLDER } from 'core/constants';

// Converted units (feet -> reels, each -> boxes) routinely land under two decimals, and this ticket
// exists because a confident "0" hid real stock. Five optional decimals matches
// NumberHelper.DEFAULT_SMALL_PRICING_DECIMALS, which getDecimalsToDisplay() already applies to
// values below 1.
export const INVENTORY_QUANTITY_FORMAT = '0,0.[00000]';

// Derived from the format rather than written as '0' so the two can never drift apart if the
// precision above ever changes.
const ZERO_RENDERED = numeral(0).format(INVENTORY_QUANTITY_FORMAT);

export interface IInventoryAvailability {
  uom: string;
  value: string;
}

export const InventoryHelper = {
  /**
   * A missing quantity means the source could not express the stock in the requested UoM, which is
   * unknown - not zero. A real 0 is a trusted value and stays a number.
   */
  isKnownQuantity: (quantity?: number | null): boolean =>
    quantity !== null && quantity !== undefined && !Number.isNaN(Number(quantity)),

  /**
   * Renders an availability figure the way every inventory surface must: an unknown quantity as
   * the placeholder with no unit, a known non-zero one as a number labelled with its unit, and a
   * zero as a bare number with no unit at all.
   *
   * Zero is unit-invariant - no stock is no stock whether you count it in feet, metres or anything
   * else - so the unit adds nothing to a zero and can actively mislead. The case that forced it:
   * a quote line was switched to a unit with no conversion path to the part's stocked unit
   * (FT -> HR). Locations holding stock correctly rendered the placeholder, because the quantity
   * genuinely could not be expressed in HR - but locations with no stock rendered "0 HR",
   * asserting a conversion we had just demonstrated we cannot perform. The number was honest, the
   * unit was not.
   *
   * This is deliberately NOT conditional on whether the unit is reachable. On the alternate path
   * we could tell, but on the legacy path a location with no stock has no inventory row and
   * therefore no source unit to test convertibility from - so the two paths would disagree, which
   * is the exact class of inconsistency this ticket exists to remove. One rule, both paths.
   */
  getInventoryAvailability: (
    quantity?: number | null,
    uom?: string | null
  ): IInventoryAvailability => {
    if (!InventoryHelper.isKnownQuantity(quantity)) {
      return { uom: '', value: DEFAULT_PLACEHOLDER };
    }

    const formatted = numeral(Number(quantity)).format(INVENTORY_QUANTITY_FORMAT);

    return {
      // Keyed off the rendered number, not the raw value: a quantity too small to survive the
      // format displays as "0", and pairing that with a unit is the "0 FT" the rule above forbids.
      // `upperCase` word-splits alphanumeric codes (FT2 -> "FT 2"), so UoMs only take `toUpper`.
      uom: formatted === ZERO_RENDERED ? '' : toUpper(uom || ''),
      value: formatted,
    };
  },

  getInventoryAvailabilityLabel: (quantity?: number | null, uom?: string | null): string => {
    const availability = InventoryHelper.getInventoryAvailability(quantity, uom);

    return [availability.value, availability.uom].filter(Boolean).join(' ');
  },
};
